import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight, ShoppingBag, Target, Play, Sparkles, ChevronDown, Globe, Star, Zap, TrendingUp, Users } from 'lucide-react';

const HeroSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showMarketInfo, setShowMarketInfo] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const heroRef = useRef(null);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleMouseMove = (e) => {
    if (!heroRef.current) return;
    const rect = heroRef.current.getBoundingClientRect();
    setMousePos({
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height
    });
  };

  return (
    <section 
      ref={heroRef}
      onMouseMove={handleMouseMove}
      className="relative min-h-screen overflow-hidden bg-[#030304]"
    >
      {/* Animated gradient orbs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div 
          className="absolute w-[800px] h-[800px] bg-amber-500/[0.08] rounded-full blur-[150px] animate-blob"
          style={{ 
            left: `${10 + mousePos.x * 10}%`, 
            top: `${5 + mousePos.y * 10}%`,
            transition: 'left 0.5s ease-out, top 0.5s ease-out'
          }}
        />
        <div 
          className="absolute w-[600px] h-[600px] bg-orange-500/[0.06] rounded-full blur-[120px] animate-blob"
          style={{ 
            animationDelay: '2s',
            right: `${5 + (1 - mousePos.x) * 10}%`, 
            bottom: `${10 + (1 - mousePos.y) * 10}%`,
            transition: 'right 0.5s ease-out, bottom 0.5s ease-out'
          }}
        />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] bg-violet-500/[0.03] rounded-full blur-[200px]" />
      </div>
      
      {/* Animated grid */}
      <div 
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(251,191,36,0.5) 1px, transparent 1px),
            linear-gradient(90deg, rgba(251,191,36,0.5) 1px, transparent 1px)
          `,
          backgroundSize: '80px 80px',
          transform: `translate(${mousePos.x * 10}px, ${mousePos.y * 10}px)`,
          transition: 'transform 0.3s ease-out'
        }}
      />

      {/* Floating particles */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1.5 h-1.5 rounded-full animate-pulse"
            style={{
              background: `linear-gradient(135deg, ${i % 3 === 0 ? '#f59e0b' : i % 3 === 1 ? '#f97316' : '#8b5cf6'} 0%, transparent 70%)`,
              left: `${10 + (i * 6)}%`,
              top: `${15 + (i * 5) % 70}%`,
              animationDelay: `${i * 0.3}s`,
              animationDuration: `${2 + i * 0.2}s`
            }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 sm:pt-24 lg:pt-28 pb-12">
        
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          
          {/* Left Content */}
          <div className="text-center lg:text-left">
            
            {/* Catalog link - Premium style */}
            <Link 
              to="/catalog"
              className={`group inline-flex items-center gap-3 px-5 py-2.5 mb-8 rounded-full bg-gradient-to-r from-emerald-500/20 via-teal-500/15 to-emerald-500/20 border border-emerald-500/40 hover:border-emerald-400 backdrop-blur-xl transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/20 hover:-translate-y-0.5 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}
              style={{ transitionDelay: '100ms' }}
            >
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                <ShoppingBag className="w-4 h-4 text-white" />
              </div>
              <span className="text-emerald-200 font-semibold">
                Опытный продавец? <span className="text-emerald-400 underline underline-offset-2">Каталог</span>
              </span>
              <ArrowRight className="w-4 h-4 text-emerald-400 group-hover:translate-x-1 transition-transform" />
            </Link>
            
            {/* Main headline - MEGA */}
            <h1 className="mb-8">
              <span className={`block text-[2.5rem] min-[400px]:text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-black text-white leading-[0.95] tracking-tight transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                Как начать
              </span>
              <span className={`block text-[2.5rem] min-[400px]:text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-black leading-[0.95] tracking-tight mt-2 transition-all duration-700 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                <span className="text-white">зарабатывать на </span>
                <span className="relative inline-block">
                  <span className="relative z-10 bg-gradient-to-r from-amber-300 via-orange-400 to-amber-300 bg-clip-text text-transparent animate-gradient bg-[length:200%_auto]">
                    Amazon
                  </span>
                  {/* Glow under Amazon */}
                  <div className="absolute -inset-2 bg-gradient-to-r from-amber-500/40 to-orange-500/40 blur-2xl -z-10 animate-pulse" />
                  {/* Underline */}
                  <svg className="absolute -bottom-2 left-0 w-full h-4" viewBox="0 0 200 16" fill="none" preserveAspectRatio="none">
                    <path d="M0 12C40 4 160 4 200 12" stroke="url(#hero-underline)" strokeWidth="6" strokeLinecap="round"/>
                    <defs>
                      <linearGradient id="hero-underline" x1="0" y1="0" x2="200" y2="0">
                        <stop offset="0%" stopColor="#f59e0b"/>
                        <stop offset="50%" stopColor="#f97316"/>
                        <stop offset="100%" stopColor="#f59e0b"/>
                      </linearGradient>
                    </defs>
                  </svg>
                </span>
              </span>
              <span className={`block text-3xl sm:text-4xl lg:text-5xl font-bold mt-4 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                <span className="text-zinc-300">в </span>
                <span className="bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent">ЕС</span>
                <span className="text-zinc-300"> и </span>
                <span className="bg-gradient-to-r from-red-400 to-rose-400 bg-clip-text text-transparent">США</span>
                <span className="text-zinc-300"> за </span>
                <span className="text-white">60 дней</span>
              </span>
            </h1>
            
            {/* Subheadline */}
            <p className={`text-xl sm:text-2xl lg:text-3xl text-zinc-400 mb-6 leading-relaxed transition-all duration-700 delay-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
              Покупаете готовые товары — продаёте через Amazon
            </p>
            
            {/* Market choice - Enhanced */}
            <button 
              onClick={() => setShowMarketInfo(!showMarketInfo)}
              className={`group flex items-center gap-3 text-lg mb-6 transition-all duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
              style={{ touchAction: 'manipulation' }}
            >
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300 ${showMarketInfo ? 'bg-gradient-to-br from-amber-500 to-orange-500' : 'bg-zinc-800 group-hover:bg-zinc-700'}`}>
                <Globe className={`w-5 h-5 transition-colors ${showMarketInfo ? 'text-black' : 'text-amber-400'}`} />
              </div>
              <span className={`font-semibold transition-colors ${showMarketInfo ? 'text-amber-400' : 'text-zinc-400 group-hover:text-white'}`}>
                Какой рынок выбрать?
              </span>
              <ChevronDown className={`w-5 h-5 transition-all duration-300 ${showMarketInfo ? 'text-amber-400 rotate-180' : 'text-zinc-500'}`} />
            </button>
            
            {/* Market info - animated */}
            <div className={`overflow-hidden transition-all duration-500 ${showMarketInfo ? 'max-h-24 opacity-100 mb-6' : 'max-h-0 opacity-0'}`}>
              <div className="p-4 rounded-xl bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30">
                <p className="text-zinc-300">
                  США или Европа — выбираете сами. <span className="text-amber-400 font-semibold">Не знаете что лучше? Поможем определиться.</span>
                </p>
              </div>
            </div>
            
            {/* Goal block - Premium */}
            <div className={`relative inline-flex items-center gap-4 px-6 py-4 mb-8 rounded-2xl overflow-hidden transition-all duration-700 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              {/* Background */}
              <div className="absolute inset-0 bg-gradient-to-r from-amber-500/15 via-orange-500/10 to-amber-500/15 backdrop-blur-xl" />
              <div className="absolute inset-0 border-2 border-amber-500/30 rounded-2xl" />
              
              <div className="relative w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/30">
                <Target className="w-6 h-6 text-white" />
              </div>
              <p className="relative text-lg text-zinc-200">
                Цель обучения — <span className="text-white font-black">первые продажи</span> и понимание, как их повторять
              </p>
            </div>
            
            {/* CTA Buttons - MEGA PREMIUM */}
            <div className={`space-y-5 transition-all duration-700 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              
              {/* Primary CTA - MAXIMUM */}
              <div className="relative group">
                {/* Outer glow */}
                <div className="absolute -inset-1 bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 rounded-2xl blur-lg opacity-70 group-hover:opacity-100 transition-opacity duration-300 animate-pulse" />
                
                <a 
                  href="#pricing"
                  data-testid="hero-cta-button"
                  className="relative flex items-center justify-center gap-4 w-full sm:w-auto h-16 sm:h-18 px-12 text-xl sm:text-2xl font-black bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 bg-[length:200%_auto] animate-gradient text-black rounded-2xl shadow-2xl shadow-amber-500/40 hover:shadow-amber-500/60 active:scale-[0.98] transition-all duration-300 overflow-hidden"
                  style={{ touchAction: 'manipulation', textShadow: '0 1px 2px rgba(255,255,255,0.3)' }}
                >
                  {/* Shine effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                  
                  <Zap className="w-6 h-6 drop-shadow-sm" />
                  <span className="drop-shadow-sm">Начать обучение</span>
                  <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform drop-shadow-sm" />
                </a>
              </div>
              
              {/* Secondary CTAs - Premium */}
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4">
                <a 
                  href="#results"
                  data-testid="hero-results-link"
                  className="group relative flex items-center gap-3 px-6 py-4 rounded-xl overflow-hidden transition-all duration-300 hover:-translate-y-1"
                  style={{ touchAction: 'manipulation' }}
                >
                  {/* Background */}
                  <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border-2 border-emerald-500/30 rounded-xl group-hover:border-emerald-500/60 transition-colors" />
                  <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/0 to-emerald-500/10 opacity-0 group-hover:opacity-100 transition-opacity" />
                  
                  <div className="relative w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <div className="relative">
                    <span className="text-white font-bold text-lg">Результаты</span>
                    <span className="block text-emerald-400 text-sm">учеников</span>
                  </div>
                  <ChevronRight className="relative w-5 h-5 text-emerald-400 group-hover:translate-x-1 transition-transform" />
                </a>
                
                <a 
                  href="#how-it-works"
                  data-testid="hero-how-link"
                  className="group relative flex items-center gap-3 px-6 py-4 rounded-xl overflow-hidden transition-all duration-300 hover:-translate-y-1"
                  style={{ touchAction: 'manipulation' }}
                >
                  {/* Background */}
                  <div className="absolute inset-0 bg-gradient-to-r from-violet-500/10 to-purple-500/10 border-2 border-violet-500/30 rounded-xl group-hover:border-violet-500/60 transition-colors" />
                  <div className="absolute inset-0 bg-gradient-to-r from-violet-500/0 to-violet-500/10 opacity-0 group-hover:opacity-100 transition-opacity" />
                  
                  <div className="relative w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center">
                    <Play className="w-5 h-5 text-white" />
                  </div>
                  <div className="relative">
                    <span className="text-white font-bold text-lg">Как это</span>
                    <span className="block text-violet-400 text-sm">работает</span>
                  </div>
                  <ChevronRight className="relative w-5 h-5 text-violet-400 group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </div>
          </div>

          {/* Right Content - Photo - MEGA PREMIUM */}
          <div className={`relative flex justify-center lg:justify-end transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
            <div className="relative">
              {/* Animated outer glow */}
              <div className="absolute -inset-8 bg-gradient-to-br from-amber-500/30 via-orange-500/20 to-violet-500/20 rounded-[3rem] blur-3xl animate-pulse" />
              
              {/* Rotating border */}
              <div 
                className="absolute -inset-2 rounded-[2.5rem] p-[3px] overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, #f59e0b, #f97316, #8b5cf6, #f59e0b)',
                  backgroundSize: '300% 300%',
                  animation: 'gradient 4s ease infinite'
                }}
              >
                <div className="w-full h-full bg-[#030304] rounded-[2.3rem]" />
              </div>
              
              {/* Image container */}
              <div className="relative rounded-[2rem] overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_amazon-course-1/artifacts/6vupjb0q_image.png"
                  alt="Вадим Малюк"
                  className="w-full max-w-[300px] sm:max-w-[350px] lg:max-w-[420px] h-auto object-cover"
                  loading="eager"
                />
                
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#030304] via-[#030304]/30 to-transparent" />
                
                {/* Author info card - Premium */}
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="relative p-4 rounded-2xl overflow-hidden">
                    {/* Glass background */}
                    <div className="absolute inset-0 bg-zinc-900/80 backdrop-blur-xl border border-zinc-700/50 rounded-2xl" />
                    
                    <div className="relative flex items-center gap-4">
                      <div className="relative">
                        <div className="w-3 h-3 rounded-full bg-emerald-500 animate-pulse" />
                        <div className="absolute inset-0 w-3 h-3 rounded-full bg-emerald-500 animate-ping" />
                      </div>
                      <div>
                        <p className="text-xl font-black text-white">Вадим Малюк</p>
                        <p className="text-amber-400 font-semibold">Amazon-продавец с 2020</p>
                      </div>
                      <div className="ml-auto flex items-center gap-1 px-3 py-1 rounded-full bg-amber-500/20 border border-amber-500/30">
                        <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                        <span className="text-amber-400 font-bold text-sm">5 лет</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Stats floating card */}
                <div className="absolute top-4 right-4 p-3 rounded-xl bg-zinc-900/80 backdrop-blur-xl border border-amber-500/30">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-emerald-400" />
                    <span className="text-white font-black">$900K+</span>
                  </div>
                  <p className="text-zinc-400 text-xs">оборот в год</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#030304] via-[#030304]/50 to-transparent pointer-events-none" />
      
      {/* Scroll indicator - hidden on mobile */}
      <div className={`absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-2 transition-all duration-1000 delay-1000 hidden lg:flex ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
        <span className="text-zinc-500 text-sm font-medium">Листайте</span>
        <div className="w-6 h-10 rounded-full border-2 border-zinc-700 flex items-start justify-center p-1.5">
          <div className="w-1.5 h-3 bg-gradient-to-b from-amber-500 to-orange-500 rounded-full animate-bounce" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
